/**
 * TagInput — inline tag editor with autocomplete.
 *
 * Type to add tags, comma/Enter to commit, click × to remove.
 */

import { useState, useRef, useCallback } from 'react'
import { X } from 'lucide-react'

interface TagInputProps {
  tags: string[]
  onChange: (tags: string[]) => void
  suggestions?: string[]
  placeholder?: string
  className?: string
}

export default function TagInput({ tags, onChange, suggestions = [], placeholder = 'Add tag...', className = '' }: TagInputProps) {
  const [input, setInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const addTag = useCallback((tag: string) => {
    const trimmed = tag.trim().toLowerCase()
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed])
    }
    setInput('')
    setShowSuggestions(false)
  }, [tags, onChange])

  const removeTag = useCallback((tag: string) => {
    onChange(tags.filter(t => t !== tag))
  }, [tags, onChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (input.trim()) addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
    if (e.key === 'Escape') {
      setInput('')
      setShowSuggestions(false)
      inputRef.current?.blur()
    }
  }

  const filtered = suggestions.filter(
    s => s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s),
  ).slice(0, 6)

  return (
    <div className={`relative ${className}`}>
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[34px] px-2 py-1.5 rq-input-dark text-sm cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map(tag => (
          <span key={tag} className="fm-tag-pill flex items-center gap-1 px-2 py-0.5 text-xs">
            {tag}
            <button
              onClick={(e) => { e.stopPropagation(); removeTag(tag) }}
              className="hover:text-[var(--rq-text)] text-[var(--rq-text-soft)] transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={e => { setInput(e.target.value); setShowSuggestions(true) }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Delay to allow suggestion click
            setTimeout(() => setShowSuggestions(false), 150)
            if (input.trim()) addTag(input)
          }}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[60px] bg-transparent outline-none text-xs text-[var(--rq-text)] placeholder:text-[var(--rq-text-soft)]/50"
        />
      </div>

      {/* Autocomplete dropdown */}
      {showSuggestions && input && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-[var(--rq-surface)] border border-white/15 rounded-lg shadow-lg py-1 max-h-36 overflow-y-auto">
          {filtered.map(s => (
            <button
              key={s}
              onMouseDown={(e) => { e.preventDefault(); addTag(s) }}
              className="w-full text-left px-3 py-1.5 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
